// API service module for all backend communication

/**
 * Get the API base URL dynamically based on the current host
 * This allows the app to work in any environment without hardcoded URLs
 * @returns API base URL in the format: protocol://host/api
 */
const getApiBaseUrl = (): string => {
  // Use environment variable if provided (for local development override)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Dynamically construct URL from current host
  const protocol = window.location.protocol; // 'http:' or 'https:'
  const host = window.location.host; // includes hostname and port if present
  return `${protocol}//${host}/api`;
};

const getCallbackUrl = (provider: string): string => {
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}/auth/callback/${provider}`;
}

const API_BASE_URL = getApiBaseUrl();

// In-memory cache for lookup data
interface LookupCache {
  countries: Country[] | null;
  cities: City[] | null;
  airports: any[] | null;
  preferences: TripPreferencesLookup | null;
  tripPricing: Map<number, any>; // Keyed by tripId
}

// Track in-flight requests to prevent duplicate calls
interface PendingRequests {
  countries: Promise<Country[]> | null;
  cities: Promise<City[]> | null;
  airports: Promise<any[]> | null;
  preferences: Promise<TripPreferencesLookup> | null;
  tripPricing: Map<number, Promise<any>>; // Keyed by tripId
}

const lookupCache: LookupCache = {
  countries: null,
  cities: null,
  airports: null,
  preferences: null,
  tripPricing: new Map(),
};

const pendingRequests: PendingRequests = {
  countries: null,
  cities: null,
  airports: null,
  preferences: null,
  tripPricing: new Map(),
};

/**
 * Clears all cached lookup data
 * Call this if you need to force a refresh of cached data
 */
export const clearLookupCache = () => {
  lookupCache.countries = null;
  lookupCache.cities = null;
  lookupCache.airports = null;
  lookupCache.preferences = null;
  lookupCache.tripPricing.clear();
  pendingRequests.countries = null;
  pendingRequests.cities = null;
  pendingRequests.airports = null;
  pendingRequests.preferences = null;
  pendingRequests.tripPricing.clear();
};

/**
 * Custom error class for authentication errors
 */
export class AuthenticationError extends Error {
  constructor(message: string = 'Authentication required. Please log in to continue.') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

/**
 * Custom error class for trip build errors (e.g., 422 validation errors)
 */
export class TripBuildError extends Error {
  status: number;
  code: string;

  constructor(message: string, code: string = 'UNKNOWN_ERROR', status: number = 422) {
    super(message);
    this.name = 'TripBuildError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Helper function to get auth token from localStorage or sessionStorage
 * @returns Auth token if available
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || null;
};

/**
 * Helper function to build headers with authentication
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object with authentication
 */
const buildAuthHeaders = (additionalHeaders: Record<string, string> = {}): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Helper function to handle API response errors
 * @param response - Fetch response object
 * @throws AuthenticationError if 401, Error for other failures
 */
const handleResponseError = async (response: Response): Promise<void> => {
  if (response.status === 401) {
    throw new AuthenticationError();
  }

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status}`);
  }
};

// Type definitions for API responses
export interface AuthResponse {
  is_authenticated: boolean;
  customer_id: number;
}

export interface CustomerData {
  id?: number;
  display_name?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  picture_url?: string;
  picture?: string;
  profile_image_url?: string;
  avatar_url?: string;
  image_url?: string;
  phone?: string;
  address?: string;
  auth_provider?: string;
  trip_mates?: any;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

export interface Trip {
  id: number;
  name?: string;
  description?: string;
  budget?: number;
  [key: string]: any;
}

export interface Country {
  id: number;
  name: string;
  code?: string;
  [key: string]: any;
}

export interface City {
  id: number;
  name: string;
  country_id: number;
  country?: string;
  latitude?: number;
  longitude?: number;
  [key: string]: any;
}

export interface POI {
  id: number;
  name: string;
  city_id: number;
  description?: string;
  latitude?: number;
  longitude?: number;
  images?: string[];
  [key: string]: any;
}

export interface Segment {
  id: number;
  trip_id: number;
  day: number;
  segment_type: string;
  poi_id?: number;
  city_id?: number;
  [key: string]: any;
}

export interface BuildTripRequest {
  country_id: number | null;
  city_ids: number[];
  duration_in_days: number | null;
  budget?: number | null;
  total_travelers?: number | null;
  cuisine_type?: string[];
  activity_type?: string[];
  name?: string;
  origin_airport?: string;
  departure_airport?: string;
  travel_mode?: string;
  arrival_date?: string;
  end_date?: string;
  preserve_city_order?: boolean;
}

export interface BuildTripFromTextRequest {
  text: string;
  preserve_city_order?: boolean;
}

export interface TripPreferencesLookup {
  activity_types: string[];
  cuisine_types: string[];
  travel_modes: string[];
  'trip-statuses': string[];
}

// Authentication APIs

export interface LoginResponse {
  token: string;
  customer_id: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  display_name: string;
}

export interface LocalLoginRequest {
  provider: 'local';
  email: string;
  password: string;
}

export interface SocialLoginRequest {
  provider: 'google' | 'facebook';
  callback_url: string;
}

/**
 * Registers a new user
 * @param email - User email
 * @param password - User password
 * @param displayName - Display name
 * @returns Login response with token and customer ID
 */
export const register = async (email: string, password: string, displayName: string): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        display_name: displayName,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || errorData.message || `Registration failed: ${response.status}`;
      throw new Error(errorMessage);
    }

    const data: LoginResponse = await response.json();

    // Store the token
    localStorage.setItem('auth_token', data.token);

    return data;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Local login with email and password
 * @param email - User email
 * @param password - User password
 * @param rememberMe - Whether to persist the session
 * @returns Login response with token and customer ID
 */
export const loginLocal = async (email: string, password: string, rememberMe: boolean = false): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        provider: 'local',
        email,
        password,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || errorData.message || 'Invalid email or password';
      throw new Error(errorMessage);
    }

    const data: LoginResponse = await response.json();

    // Store the token based on rememberMe preference
    if (rememberMe) {
      localStorage.setItem('auth_token', data.token);
    } else {
      sessionStorage.setItem('auth_token', data.token);
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Social login with Google or Facebook
 * Opens a popup for OAuth flow
 * @param provider - 'google' or 'facebook'
 * @param rememberMe - Whether to persist the session
 * @returns Promise that resolves when auth is complete
 */
export const loginSocial = async (provider: 'google' | 'facebook', rememberMe: boolean = false): Promise<void> => {
  try {
    // Store rememberMe preference for the popup callback
    sessionStorage.setItem('auth_remember_me', rememberMe ? 'true' : 'false');

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        provider,
        callback_url: getCallbackUrl(provider),
      }),
    });

    if (!response.ok) {
      throw new Error(`Social login request failed: ${response.status}`);
    }

    const data = await response.json();
    const redirectUrl = data.auth_url;

    if (!redirectUrl) {
      throw new Error('No redirect URL received from server');
    }

    // Open OAuth in a popup
    const width = 520;
    const height = 700;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    const popup = window.open(
      redirectUrl,
      `${provider}Login`,
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      throw new Error('Popup blocked. Please allow popups for this site.');
    }

    // Return a promise that resolves when OAuth is complete
    return new Promise<void>((resolve, reject) => {
      let completed = false;

      // Timeout after 5 minutes if no response (user abandoned login)
      const timeout = setTimeout(() => {
        if (!completed) {
          completed = true;
          window.removeEventListener('message', handleMessage);
          reject(new AuthenticationError('Login timed out'));
        }
      }, 5 * 60 * 1000);

      const cleanup = () => {
        clearTimeout(timeout);
        window.removeEventListener('message', handleMessage);
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (completed) return;

        if (event.data?.type === 'AUTH_CALLBACK') {
          completed = true;
          cleanup();
          resolve();
        } else if (event.data?.type === 'AUTH_ERROR') {
          completed = true;
          cleanup();
          reject(new Error(event.data.error || 'Authentication failed'));
        }
      };

      window.addEventListener('message', handleMessage);
    });
  } catch (error) {
    console.error('Social login error:', error);
    throw error;
  }
};

/**
 * Legacy login function - now uses local login
 * @deprecated Use loginLocal or loginSocial instead
 */
export const login = async (): Promise<number> => {
  throw new Error('Legacy login not supported. Use loginLocal or loginSocial.');
};

/**
 * Legacy authenticate function
 * @deprecated Use loginLocal or loginSocial instead
 */
export const authenticate = async (authParams: string): Promise<number> => {
  console.warn('Legacy authenticate called - this should not happen with new auth flow');
  return 0;
};

/**
 * Logs out the current user
 * @returns Promise that resolves when logout is complete
 */
export const logout = async (): Promise<void> => {
  // Clear local tokens
  localStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_token');

  try {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      console.error('Logout API call failed:', response.status);
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Don't throw - user is already logged out locally
  }
};

/**
 * Request a password reset email
 * @param email - User email address
 * @returns Success message
 */
export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to send password reset email');
    }

    const data = await response.json();
    return data.data || { message: 'If an account exists with this email, a password reset link has been sent.' };
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
};

/**
 * Reset password with token
 * @param token - Reset token from email link
 * @param newPassword - New password
 * @returns Success message
 */
export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to reset password');
    }

    const data = await response.json();
    return data.data || { message: 'Password has been reset successfully.' };
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

// Feedback API

export interface FeedbackRequest {
  rating: number;
  message: string;
  email?: string;
  category?: 'bug' | 'feature' | 'general';
  captcha_token: string;
}

/**
 * Submit user feedback
 * @param feedback - Feedback data including rating, message, and optional fields
 * @returns Success response
 */
export const submitFeedback = async (feedback: FeedbackRequest): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(feedback),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to submit feedback');
    }

    const data = await response.json();
    return data.data || { message: 'Feedback submitted successfully' };
  } catch (error) {
    console.error('Submit feedback error:', error);
    throw error;
  }
};

// Share Trip API

export interface ShareTripRequest {
  trip_id: number;
  email: string;
  can_edit: boolean;
}

export interface SharedWithUser {
  email: string;
  can_edit: boolean;
  share_token: string;
}

export interface UpdateShareRequest {
  share_token: string;
  can_edit: boolean;
}

export interface RemoveShareRequest {
  share_token: string;
}

/**
 * Share a trip with another user via email
 * @param request - Share trip request with trip ID, email, and permissions
 * @returns Success response
 */
export const shareTrip = async (request: ShareTripRequest): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/share`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || errorData.message || 'Failed to share trip');
    }

    const data = await response.json();
    return data.data || { message: 'Trip shared successfully' };
  } catch (error) {
    console.error('Share trip error:', error);
    throw error;
  }
};

/**
 * Update share permissions for a trip
 * @param request - Update share request with share token and new permissions
 * @returns Success response
 */
export const updateTripShare = async (request: UpdateShareRequest): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/share/${request.share_token}`, {
      method: 'PATCH',
      headers: buildAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ can_edit: request.can_edit }),
    });

    // 204 No Content is success
    if (response.status === 204) {
      return { message: 'Share updated successfully' };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || errorData.message || 'Failed to update share');
    }

    return { message: 'Share updated successfully' };
  } catch (error) {
    console.error('Update share error:', error);
    throw error;
  }
};

/**
 * Remove share access for a trip
 * @param request - Remove share request with share token
 * @returns Success response
 */
export const removeTripShare = async (request: RemoveShareRequest): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/share/${request.share_token}`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    // 204 No Content is success
    if (response.status === 204) {
      return { message: 'Share removed successfully' };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || errorData.message || 'Failed to remove share');
    }

    const data = await response.json().catch(() => ({}));
    return data.data || { message: 'Share removed successfully' };
  } catch (error) {
    console.error('Remove share error:', error);
    throw error;
  }
};

// Customer APIs

/**
 * Fetches the current customer's data
 * @returns Customer data object
 */
export const fetchCustomerData = async (): Promise<CustomerData> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customers`, {
      method: 'GET',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    await handleResponseError(response);

    const data: CustomerData = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching customer data:', error);
    throw error;
  }
};

// Trip APIs

/**
 * Fetches all system trips (public/template trips)
 * @returns Array of system trips
 */
export const fetchSystemTrips = async (): Promise<Trip[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/system`);
    if (!response.ok) throw new Error('Failed to fetch system trips');
    const data = await response.json();

    if (!data || data === null) {
      return [];
    }

    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching system trips:', error);
    throw error;
  }
};

/**
 * Fetches trips for the current customer
 * @returns Array of customer trips
 */
export const fetchCustomerTrips = async (): Promise<Trip[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customers/trips`, {
      method: 'GET',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    await handleResponseError(response);

    const data = await response.json();

    if (!data || data === null) {
      return [];
    }

    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching customer trips:', error);
    throw error;
  }
};

/**
 * Builds a new trip with specified parameters
 * @param tripRequest - Trip build request parameters
 * @returns Trip data object
 */
export const buildTrip = async (tripRequest: BuildTripRequest): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/build`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(tripRequest),
    });

    if (!response.ok) {
      // Try to parse error response for detailed message
      try {
        const errorData = await response.json();
        // Support both formats: { code, message } and { error: { code, message } }
        const error = errorData.error || errorData;
        const message = typeof error.message === 'string' ? error.message : error.message?.message || 'Failed to build trip';
        const code = error.code || error.message?.code || 'UNKNOWN_ERROR';
        throw new TripBuildError(message, code, response.status);
      } catch (parseError) {
        if (parseError instanceof TripBuildError) {
          throw parseError;
        }
      }
      throw new Error('Failed to build trip');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error building trip:', error);
    throw error;
  }
};

/**
 * Builds a new trip from natural language text description
 * @param request - Object containing the text description
 * @returns Trip data object
 */
export const buildTripFromText = async (request: BuildTripFromTextRequest): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/build-from-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      // Try to parse error response for detailed message
      try {
        const errorData = await response.json();
        // Support both formats: { code, message } and { error: { code, message } }
        const error = errorData.error || errorData;
        const message = typeof error.message === 'string' ? error.message : error.message?.message || 'Failed to build trip from text';
        const code = error.code || error.message?.code || 'UNKNOWN_ERROR';
        throw new TripBuildError(message, code, response.status);
      } catch (parseError) {
        if (parseError instanceof TripBuildError) {
          throw parseError;
        }
      }
      throw new Error('Failed to build trip from text');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error building trip from text:', error);
    throw error;
  }
};

/**
 * Fetches segments for a specific trip
 * @param tripId - Trip ID
 * @returns Array of trip segments
 */
export const fetchTripSegments = async (tripId: number): Promise<Segment[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/${tripId}/segments`);

    if (!response.ok) {
      throw new Error('Failed to fetch trip segments');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching trip segments:', error);
    throw error;
  }
};

// Location APIs

/**
 * Fetches all countries
 * @returns Array of countries
 */
export const fetchCountries = async (): Promise<Country[]> => {
  // Return cached data if available
  if (lookupCache.countries) {
    return lookupCache.countries;
  }

  // Return existing in-flight request if one exists
  if (pendingRequests.countries) {
    return pendingRequests.countries;
  }

  // Create new request and store the promise
  pendingRequests.countries = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/countries`);

      if (!response.ok) {
        throw new Error('Failed to fetch countries');
      }

      const data = await response.json();
      const countries = Array.isArray(data) ? data : [];

      // Cache the result
      lookupCache.countries = countries;
      return countries;
    } catch (error) {
      console.error('Error fetching countries:', error);
      throw error;
    } finally {
      // Clear pending request
      pendingRequests.countries = null;
    }
  })();

  return pendingRequests.countries;
};

/**
 * Fetches all cities
 * @returns Array of cities
 */
export const fetchCities = async (): Promise<City[]> => {
  // Return cached data if available
  if (lookupCache.cities) {
    return lookupCache.cities;
  }

  // Return existing in-flight request if one exists
  if (pendingRequests.cities) {
    return pendingRequests.cities;
  }

  // Create new request and store the promise
  pendingRequests.cities = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/cities`);

      if (!response.ok) {
        throw new Error('Failed to fetch cities');
      }

      const data = await response.json();
      const cities = Array.isArray(data) ? data : [];

      // Cache the result
      lookupCache.cities = cities;
      return cities;
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw error;
    } finally {
      // Clear pending request
      pendingRequests.cities = null;
    }
  })();

  return pendingRequests.cities;
};

/**
 * Fetches all airports
 * @returns Array of airports
 */
export const fetchAirports = async (): Promise<any[]> => {
  // Return cached data if available
  if (lookupCache.airports) {
    return lookupCache.airports;
  }

  // Return existing in-flight request if one exists
  if (pendingRequests.airports) {
    return pendingRequests.airports;
  }

  // Create new request and store the promise
  pendingRequests.airports = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/airports`);

      if (!response.ok) {
        throw new Error('Failed to fetch airports');
      }

      const data = await response.json();
      const airports = Array.isArray(data) ? data : [];

      // Cache the result
      lookupCache.airports = airports;
      return airports;
    } catch (error) {
      console.error('Error fetching airports:', error);
      throw error;
    } finally {
      // Clear pending request
      pendingRequests.airports = null;
    }
  })();

  return pendingRequests.airports;
};

// POI APIs

/**
 * Fetches points of interest for specified cities and days
 * @param cities - Array of city IDs
 * @param days - Number of days
 * @returns Array of POIs
 */
export const fetchPointsOfInterest = async (cities: number[], days: number): Promise<POI[]> => {
  try {
    const citiesParam = cities.join(',');
    const response = await fetch(`${API_BASE_URL}/points-of-interest?cities=${citiesParam}&days=${days}`);

    if (!response.ok) {
      throw new Error('Failed to fetch points of interest');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching points of interest:', error);
    throw error;
  }
};

/**
 * Fetches POIs for a specific country
 * @param countryId - Country ID
 * @returns Array of POIs
 */
export const fetchPOIsByCountry = async (countryId: number): Promise<POI[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/pois/country/${countryId}`);

    if (!response.ok) {
      throw new Error('Failed to fetch POIs by country');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching POIs by country:', error);
    throw error;
  }
};

/**
 * Saves a trip to the customer's account
 * @param tripId - Trip ID to save
 * @returns Saved trip data
 */
export const saveTripToCustomer = async (tripId: number): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customers/add_my_trips`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        trip_id: tripId
      })
    });

    await handleResponseError(response);

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error saving trip to customer:', error);
    throw error;
  }
};

/**
 * Fetches trip details by ID
 * @param tripId - Trip ID
 * @returns Trip details
 */
export const fetchTripById = async (tripId: number): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/${tripId}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    await handleResponseError(response);

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching trip by ID:', error);
    throw error;
  }
};

/**
 * Deletes a trip
 * @param tripId - Trip ID to delete
 * @returns Success status
 */
export const deleteTripFromCustomer = async (tripId: number): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/${tripId}`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    await handleResponseError(response);
  } catch (error) {
    console.error('Error deleting trip:', error);
    throw error;
  }
};

/**
 * Updates a trip with new details
 * @param tripId - Trip ID to update
 * @param updates - Object containing trip fields to update
 * @returns Updated trip data or status code
 */
export const updateTrip = async (tripId: number, updates: any): Promise<number> => {
  try {
    const updatedTrip: any = {
      id: tripId,
      ...updates
    };

    // Format arrival_date if present
    if (updates.arrival_date) {
      const d = new Date(updates.arrival_date);
      updatedTrip.arrival_date = d.toISOString();
    }

    // Ensure numeric fields are properly typed
    if (updates.duration_in_days !== undefined) {
      updatedTrip.duration_in_days = parseInt(String(updates.duration_in_days));
    }
    if (updates.budget !== undefined) {
      updatedTrip.budget = parseInt(String(updates.budget));
    }
    if (updates.total_travelers !== undefined) {
      updatedTrip.total_travelers = parseInt(String(updates.total_travelers));
    }

    const response = await fetch(`${API_BASE_URL}/trips/${tripId}`, {
      method: 'PUT',
      headers: buildAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(updatedTrip),
    });

    await handleResponseError(response);

    return 200;
  } catch (error) {
    console.error('Error updating trip:', error);
    throw error;
  }
};

/**
 * Gets a signed URL for uploading images to Google Cloud Storage
 * @param resourceType - Type of resource (e.g., 'trips', 'pois')
 * @param imageType - Image file extension (e.g., 'png', 'jpg')
 * @returns Signed URL and final image URL
 */
export const getImageSignature = async (resourceType: string = 'trips', imageType: string = 'png'): Promise<{ signed_url: string; image_url: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/images/signature?resource_type=${resourceType}&image_type=${imageType}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    await handleResponseError(response);

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting image signature:', error);
    throw error;
  }
};

/**
 * Uploads an image file to Google Cloud Storage
 * @param file - Image file to upload
 * @param resourceType - Type of resource (e.g., 'trips', 'pois')
 * @returns Image URL or null if upload failed
 */
export const uploadImageToGCS = async (file: File, resourceType: string = 'trips'): Promise<{ status: number; url: string | null }> => {
  try {
    // Determine image type from file
    const imageType = file.type.split('/')[1] || 'png';

    // Step 1: Get signed URL from backend
    const { signed_url, image_url } = await getImageSignature(resourceType, imageType);

    // Step 2: Upload file to Google Cloud Storage using signed URL
    const uploadResponse = await fetch(signed_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload to GCS: ${uploadResponse.status}`);
    }

    // Step 3: Return the image URL
    return {
      status: 200,
      url: image_url
    };
  } catch (error) {
    console.error('Error uploading image to GCS:', error);
    return {
      status: 500,
      url: null
    };
  }
};

/**
 * Reorders segments by swapping positions
 * @param sourceId - ID of the segment to move
 * @param destId - ID of the segment to swap with
 * @returns Success status
 */
export const reorderSegments = async (sourceId: number, destId: number): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/segments/reorder`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        source_id: sourceId,
        dest_id: destId
      })
    });

    await handleResponseError(response);
  } catch (error) {
    console.error('Error reordering segments:', error);
    throw error;
  }
};

/**
 * Deletes a segment
 * @param segmentId - ID of the segment to delete
 * @returns Success status
 */
export const deleteSegment = async (segmentId: number): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/segments/${segmentId}`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    await handleResponseError(response);
  } catch (error) {
    console.error('Error deleting segment:', error);
    throw error;
  }
};

/**
 * Fetches all POIs for a specific city
 * @param cityId - ID of the city
 * @returns Array of POIs for the city
 */
export const fetchPOIsByCity = async (cityId: number): Promise<POI[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cities/${cityId}/pois`);

    if (!response.ok) {
      throw new Error('Failed to fetch city POIs');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching city POIs:', error);
    throw error;
  }
};

/**
 * Adds a segment to a trip
 * @param tripId - ID of the trip
 * @param segmentData - Segment data to add
 * @returns Created segment
 */
export const addSegmentToTrip = async (tripId: number, segmentData: any): Promise<Segment> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/${tripId}/segments`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(segmentData),
    });

    await handleResponseError(response);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error adding segment to trip:', error);
    throw error;
  }
};

/**
 * Rebuilds a trip with new preferences (creates a new trip)
 * @param preferences - New trip preferences
 * @returns New trip data
 */
export const rebuildTrip = async (preferences: BuildTripRequest): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/build`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(preferences),
    });

    if (response.status === 401) {
      throw new AuthenticationError();
    }

    if (!response.ok) {
      // Try to parse error response for detailed message
      try {
        const errorData = await response.json();
        // Support both formats: { code, message } and { error: { code, message } }
        const error = errorData.error || errorData;
        const message = typeof error.message === 'string' ? error.message : error.message?.message || 'Failed to rebuild trip';
        const code = error.code || error.message?.code || 'UNKNOWN_ERROR';
        throw new TripBuildError(message, code, response.status);
      } catch (parseError) {
        if (parseError instanceof TripBuildError) {
          throw parseError;
        }
      }
      throw new Error('Failed to rebuild trip');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error rebuilding trip:', error);
    throw error;
  }
};

/**
 * Fetches trip preferences lookup data
 * @returns Trip preferences lookup data
 */
export const fetchTripPreferences = async (): Promise<TripPreferencesLookup> => {
  // Return cached data if available
  if (lookupCache.preferences) {
    return lookupCache.preferences;
  }

  // Return existing in-flight request if one exists
  if (pendingRequests.preferences) {
    return pendingRequests.preferences;
  }

  // Create new request and store the promise
  pendingRequests.preferences = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/trips/preferences`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch trip preferences');
      }

      const data = await response.json();

      // Cache the result
      lookupCache.preferences = data;
      return data;
    } catch (error) {
      console.error('Error fetching trip preferences:', error);
      throw error;
    } finally {
      // Clear pending request
      pendingRequests.preferences = null;
    }
  })();

  return pendingRequests.preferences;
};

/**
 * Downloads trip as PDF
 * @param tripId - Trip ID
 * @param tripName - Optional trip name for the filename
 */
export const downloadTripPdf = async (tripId: number, tripName?: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/trips/${tripId}/pdf`, {
      method: 'GET',
      headers: buildAuthHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to download trip PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = tripName ? `${tripName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf` : `trip_${tripId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading trip PDF:', error);
    throw error;
  }
};

/**
 * Fetches trip pricing breakdown
 * @param tripId - Trip ID
 * @returns Trip pricing data
 */
export const fetchTripPricing = async (tripId: number): Promise<any> => {
  // Return cached data if available
  if (lookupCache.tripPricing.has(tripId)) {
    return lookupCache.tripPricing.get(tripId);
  }

  // Return existing in-flight request if one exists
  if (pendingRequests.tripPricing.has(tripId)) {
    return pendingRequests.tripPricing.get(tripId);
  }

  // Create new request and store the promise
  const request = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/trips/${tripId}/price`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch trip pricing');
      }

      const data = await response.json();

      // Cache the result
      lookupCache.tripPricing.set(tripId, data);
      return data;
    } catch (error) {
      console.error('Error fetching trip pricing:', error);
      throw error;
    } finally {
      // Clear pending request
      pendingRequests.tripPricing.delete(tripId);
    }
  })();

  pendingRequests.tripPricing.set(tripId, request);
  return request;
};
